import { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import {
  useInvoiceBillingStatusLazyQuery,
  useSetupInvoiceBillingMutation,
  useVenuesQuery,
} from '../../generated/graphql';

type VenueOption = { id: string; name: string };

const CURRENCY_OPTIONS = ['CZK', 'EUR', 'GBP', 'PLN', 'SEK', 'DKK', 'NOK'];

const BILLING_FREQUENCY_OPTIONS = [
  { value: 1, label: 'Monthly' },
  { value: 3, label: 'Quarterly' },
  { value: 12, label: 'Yearly' },
] as const;

export const InvoiceBillingManager = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [ico, setIco] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [amountWithoutVat, setAmountWithoutVat] = useState<number | ''>('');
  const [currency, setCurrency] = useState('CZK');
  const [billingFrequencyMonths, setBillingFrequencyMonths] = useState(12);
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [vatRate, setVatRate] = useState(21);
  const [invoiceItemName, setInvoiceItemName] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: venuesData, loading: venuesLoading } = useVenuesQuery();
  const [loadStatus, { data: statusData, loading: statusLoading }] =
    useInvoiceBillingStatusLazyQuery();
  const [setupBilling, { loading: setupLoading }] =
    useSetupInvoiceBillingMutation();

  const venues: VenueOption[] = useMemo(() => {
    if (!venuesData?.venues) return [];
    const unique = new Map(
      venuesData.venues.map((venue) => [
        venue.id,
        { id: venue.id, name: venue.name },
      ]),
    );
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;
  const billingStatus = statusData?.invoiceBillingStatus;

  const handleVenueChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: VenueOption | null,
  ) => {
    const newVenueId = value?.id ?? '';
    setSelectedVenueId(newVenueId);

    if (value) {
      loadStatus({ variables: { venueId: value.id } });
    }
  };

  const handleSetup = async () => {
    if (!selectedVenueId || !billingEmail || !amountWithoutVat) return;

    try {
      await setupBilling({
        variables: {
          input: {
            venueId: selectedVenueId,
            companyName: companyName || undefined,
            ico: ico || undefined,
            billingEmail,
            ccEmail: ccEmail || undefined,
            amountWithoutVat,
            currency,
            billingFrequencyMonths,
            startDate: (startDate as DateTime).toISODate() as string,
            invoiceItemName: invoiceItemName || undefined,
            vatRate,
          },
        },
        toastConfig: {
          successText: 'Invoice billing set up successfully',
          errorText: 'Failed to set up invoice billing',
        },
      });

      loadStatus({ variables: { venueId: selectedVenueId } });
    } catch (error) {
      console.error('Failed to set up invoice billing:', error);
    }

    setConfirmOpen(false);
  };

  const canSetup =
    selectedVenueId &&
    billingEmail &&
    amountWithoutVat &&
    startDate !== null &&
    !setupLoading;

  const frequencyLabel =
    BILLING_FREQUENCY_OPTIONS.find((o) => o.value === billingFrequencyMonths)
      ?.label ?? `${billingFrequencyMonths} months`;

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box width="100%" p={3} display="flex" justifyContent="center">
        <Box width="100%" maxWidth={800}>
          <Typography variant="h4" gutterBottom>
            Invoice Billing
          </Typography>

          {/* Venue Selection */}
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Venue
            </Typography>
            <Autocomplete<VenueOption>
              fullWidth
              value={selectedVenue}
              options={venues}
              getOptionLabel={(o) => o.name}
              loading={venuesLoading}
              onChange={handleVenueChange}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Venue"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: venuesLoading ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                />
              )}
            />
          </Card>

          {statusLoading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          {/* Warnings */}
          {selectedVenueId && !statusLoading && billingStatus && (
            <>
              {billingStatus.hasStripeBilling && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  This venue also has Stripe billing. Ensure this is
                  intentional.
                </Alert>
              )}

              {/* Existing billing info */}
              {billingStatus.isEnabled && (
                <Card sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Existing Invoice Billing
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Fakturoid Subject ID:</strong>{' '}
                    {billingStatus.externalCustomerId}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Legal Data Filled:</strong>{' '}
                    {billingStatus.isLegalDataFilledIn ? 'Yes' : 'No'}
                  </Typography>
                  <Link
                    href={`https://app.fakturoid.cz/subjects/${billingStatus.externalCustomerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View in Fakturoid
                  </Link>
                </Card>
              )}

              {/* Setup form */}
              {!billingStatus.isEnabled && (
                <Card sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Set Up Invoice Billing
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Company
                  </Typography>

                  <TextField
                    fullWidth
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    helperText="Defaults to venue name if empty"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="ICO (Registration Number)"
                    value={ico}
                    onChange={(e) => setIco(e.target.value)}
                    helperText="Optional. Enables ARES auto-fill for CZ companies"
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Contact
                  </Typography>

                  <TextField
                    fullWidth
                    required
                    label="Billing Email"
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="CC Email"
                    type="email"
                    value={ccEmail}
                    onChange={(e) => setCcEmail(e.target.value)}
                    helperText="Optional. Defaults to hello@dishboard.cz in Fakturoid"
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Pricing & Schedule
                  </Typography>

                  <Box display="flex" gap={2} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Amount excl. VAT"
                      value={amountWithoutVat}
                      onChange={(e) =>
                        setAmountWithoutVat(
                          e.target.value === '' ? '' : Number(e.target.value),
                        )
                      }
                      inputProps={{ min: 0, step: 1 }}
                    />

                    <TextField
                      select
                      label="Currency"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      sx={{ minWidth: 120 }}
                    >
                      {CURRENCY_OPTIONS.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  <TextField
                    fullWidth
                    type="number"
                    label="VAT Rate (%)"
                    value={vatRate}
                    onChange={(e) => setVatRate(Number(e.target.value))}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    select
                    fullWidth
                    label="Billing Frequency"
                    value={billingFrequencyMonths}
                    onChange={(e) =>
                      setBillingFrequencyMonths(Number(e.target.value))
                    }
                    sx={{ mb: 2 }}
                  >
                    {BILLING_FREQUENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(value: DateTime | null) => setStartDate(value)}
                    disableMaskedInput
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  <TextField
                    fullWidth
                    label="Invoice Item Name"
                    value={invoiceItemName}
                    onChange={(e) => setInvoiceItemName(e.target.value)}
                    helperText='Defaults to "Dishboard subscription"'
                    sx={{ mb: 2 }}
                  />

                  <Button
                    variant="contained"
                    onClick={() => setConfirmOpen(true)}
                    disabled={!canSetup}
                    sx={{ mt: 2 }}
                  >
                    Set Up Invoice Billing
                  </Button>
                </Card>
              )}
            </>
          )}

          {/* Confirmation Dialog */}
          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
            <DialogTitle>Confirm Invoice Billing Setup</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                Set up invoice billing for{' '}
                <strong>{selectedVenue?.name}</strong>?
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Company:</strong> {companyName || selectedVenue?.name}
                </Typography>
                {ico && (
                  <Typography variant="body2">
                    <strong>ICO:</strong> {ico}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Billing Email:</strong> {billingEmail}
                </Typography>
                {ccEmail && (
                  <Typography variant="body2">
                    <strong>CC Email:</strong> {ccEmail}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Amount excl. VAT:</strong> {amountWithoutVat}{' '}
                  {currency}
                </Typography>
                <Typography variant="body2">
                  <strong>VAT Rate:</strong> {vatRate}%
                </Typography>
                <Typography variant="body2">
                  <strong>Frequency:</strong> {frequencyLabel}
                </Typography>
                <Typography variant="body2">
                  <strong>Start Date:</strong>{' '}
                  {startDate?.toLocaleString(DateTime.DATE_MED)}
                </Typography>
                <Typography variant="body2">
                  <strong>Invoice Item:</strong>{' '}
                  {invoiceItemName || 'Dishboard subscription'}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSetup}
                variant="contained"
                disabled={setupLoading}
              >
                {setupLoading ? <CircularProgress size={20} /> : 'Confirm'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};
